import type { Error as PlayError } from '@empathybroker/tsclient-play/dist/models/error'
import type { Facet } from '@empathybroker/tsclient-play/dist/models/facet'
import type { FacetValue } from '@empathybroker/tsclient-play/dist/models/facet-value'
import type { RegularFilterValueConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-value-config'
import type { PlayResponse } from '@empathybroker/tsclient-play/dist/models/play-response'
import type { RankingElement } from '@empathybroker/tsclient-play/dist/models/ranking-element'
import type { List } from '@empathybroker/tsclient-play/dist/models/search/list'
import type { Result } from '@empathybroker/tsclient-play/dist/models/search/result'
import type { Pagination, RegularFilter, RegularFilterSelectedValues } from '../models'
// @ts-strict-ignore
import { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { PermissionLevelEnum } from '@empathybroker/tsclient-play/dist/enums/permission-level.enum'
import { RankingActionEnum } from '@empathybroker/tsclient-play/dist/enums/ranking-action.enum'
import { DomainDateUtils } from '@empathyco/playboard-core-domain'

import Vue from 'vue'

import {
  ConfigurationToolIdEnum,
  FeatureIdEnum,
  RestrictedValueEnum,
  SearchDataLoadTypeEnum,
} from '../enums'
import { CrudFormMixin } from '../mixins/crud-form.mixin'

import { RestrictedMixin } from '../mixins/restricted.mixin'

import { DateUtils } from '../utils'

export const ConfigurationsFormMixin = {
  mixins: [CrudFormMixin, RestrictedMixin],
  computed: {
    readOnly(): boolean {
      if (CrudFormMixin.computed.readOnly.bind(this)()) {
        return true
      }
      const restrictedValue = this.getRestrictedValueFromObject(this.item.restricted)
      return restrictedValue === RestrictedValueEnum.READ
    },
    valid(): boolean {
      return (
        this.loadSearchDataEnabled &&
        ((!!this.item.ranking.products && !!this.item.ranking.products.elements.length) ||
          (!!this.item.ranking.attributes && !!this.item.ranking.attributes.elements.length) ||
          (!!this.item.facets && !!this.item.facets.elements.length))
      )
    },
    searchFilters(): RegularFilter[] {
      return Object.values(this.pivots)
        .map((filter: RegularFilter) => ({
          ...filter,
          config: {
            ...filter.config,
            values: this.getSearchFilterValueConfigs(filter),
          },
        }))
        .concat(Object.values(this.filters))
        .map((filter: RegularFilter) => ({
          ...filter,
          config: {
            ...filter.config,
            mandatory: true,
            multiselectDisabled: !this.instanceConfig.allowMultipleValuesInSearchFilters,
          },
          selectedValues: this.getSearchFilterSelectedValues(filter),
        }))
    },
    visibleSearchFilters(): RegularFilter[] {
      return this.searchFilters.filter((filter: RegularFilter) => filter.config.values.length > 1)
    },
    loadSearchDataEnabled(): boolean {
      return Object.values(this.pivots).every(
        (pivot: RegularFilter) => !!pivot.selectedValues.length,
      )
    },
    tools(): ConfigurationToolIdEnum[] {
      const tools = [
        ConfigurationToolIdEnum.ATTRIBUTES_RANKING,
        ConfigurationToolIdEnum.PRODUCTS_RANKING,
      ]

      const isActivateFacetsTool = this.instanceConfig.domains[DomainEnum.GLOBAL].permissions.find(
        permission =>
          permission.id === FeatureIdEnum.FACETS && permission.level !== PermissionLevelEnum.NONE,
      )

      if (this.context.domain === DomainEnum.BROWSE && !isActivateFacetsTool) {
        tools.unshift(ConfigurationToolIdEnum.FACETS_CONFIG)
      }
      return tools
    },
    resultsPreviewParams() {
      const params: any = {
        productsRankingField: this.instanceConfig.catalog.resultMappings.id,
      }
      if (this.item.ranking.products) {
        const productRankingIds = this.item.ranking.products.elements.reduce(
          (productIds, element: RankingElement) => {
            if (this.isRankingElementMatchingWithPreview(element)) {
              const productId = element.fieldValue
              if (element.action === RankingActionEnum.BOOST) {
                productIds.boosted.push(productId)
              } else if (element.action === RankingActionEnum.BURY) {
                productIds.buried.push(productId)
              }
            }
            return productIds
          },
          {
            boosted: [],
            buried: [],
          },
        )
        params.productsRankingBoostsIds = productRankingIds.boosted.join()
        params.productsRankingBuriesIds = productRankingIds.buried.join()
      }
      if (this.item.ranking.attributes) {
        const attributeRankingParams = this.item.ranking.attributes.elements.reduce(
          (attributeParams, element: RankingElement) => {
            if (this.isRankingElementMatchingWithPreview(element)) {
              const attributeParam = `${element.fieldName}:${element.fieldValue}`
              if (element.action === RankingActionEnum.BOOST) {
                attributeParams.boosted.push(attributeParam)
              } else if (element.action === RankingActionEnum.BURY) {
                attributeParams.buried.push(attributeParam)
              }
            }
            return attributeParams
          },
          {
            boosted: [],
            buried: [],
          },
        )
        params.attributesRankingBoosts = attributeRankingParams.boosted.join()
        params.attributesRankingBuries = attributeRankingParams.buried.join()
      }
      return params
    },
    facetsPreviewParams() {
      if (this.item.facets) {
        return this.item.facets.elements.reduce(
          (params, facet: Facet) => {
            params.facet.push(facet.id)
            params[`f.${facet.id}.sort`] = facet.sortType ?? ''
            Object.values(RankingActionEnum).forEach((action: RankingActionEnum) => {
              params[this.getFacetActionParamName(facet.id, action)] = ''
            })
            if (facet.values) {
              facet.values.forEach((facetValue: FacetValue) => {
                const facetActionParamName = this.getFacetActionParamName(
                  facet.id,
                  facetValue.action,
                )
                if (!params[facetActionParamName]) {
                  params[facetActionParamName] = facetValue.id
                } else {
                  params[facetActionParamName] += `,${facetValue.id}`
                }
              })
            }
            return params
          },
          { facet: [] },
        )
      }
    },
    loadPreviewDisabled(): boolean {
      return false
    },
  },
  data() {
    return {
      dataType: ActionTypeEnum.CONFIGURATION,
      item: {
        enabled: true,
        name: undefined,
        context: {
          pivots: {},
        },
        ranking: {
          products: {
            elements: [],
          },
          attributes: {
            elements: [],
          },
        },
        facets: {
          elements: [],
        },
        restricted: {},
      },
      searchData: {
        results: [],
        facets: [],
        loading: {
          [SearchDataLoadTypeEnum.RESULTS]: false,
          [SearchDataLoadTypeEnum.FACETS]: false,
        },
        error: undefined,
      },
      searchFiltersSelectedValues: {},
      pagination: {
        page: 1,
        itemsPerPage: 20,
        totalItems: 0,
      },
      watchers: {},
      messages: this.$epb.messages.tools.configurations,
      searchDate: undefined,
    }
  },
  methods: {
    async onItemLoad(): Promise<void> {
      await CrudFormMixin.methods.onItemLoad.bind(this)()
      this.watchers.pivots = this.$watch('item.context.pivots', this.onContextPivotsWatch, {
        deep: true,
      })
      this.watchers.searchFilters = this.$watch('searchFilters', this.onSearchFiltersWatch, {
        deep: true,
      })
      this.watchers.resultsPreview = this.$watch(
        'resultsPreviewParams',
        this.onResultsPreviewParamsWatch,
        { deep: true },
      )
      this.watchers.facetsPreview = this.$watch(
        'facetsPreviewParams',
        this.onFacetsPreviewParamsWatch,
        { deep: true },
      )
      await this.loadSearchData({ resetPagination: true })
    },
    async loadSearchData({ loadType, resetPagination }): Promise<void> {
      if (this.loadSearchDataEnabled) {
        if (resetPagination) {
          this.pagination.page = 1
        }
        this.setSearchDataLoading(loadType, true)
        try {
          const searchResponse = await this.fetchSearchData()
          this.searchData.results = searchResponse.data.items
          this.pagination.totalItems = searchResponse.data.total
          // TODO don't override facets if user query is set
          this.searchData.facets = searchResponse.data.facets
        } catch (error) {
          this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
          this.searchData.results = []
          this.pagination.totalItems = 0
          this.searchData.facets = []
        } finally {
          this.setSearchDataLoading(loadType, false)
        }
      }
    },
    setSearchDataLoading(loadType: SearchDataLoadTypeEnum, loading: boolean): void {
      if (loadType) {
        this.searchData.loading[loadType] = loading
      } else {
        Object.keys(this.searchData.loading).forEach(
          (searchDataLoadType: SearchDataLoadTypeEnum) => {
            this.searchData.loading[searchDataLoadType] = loading
          },
        )
      }
    },
    async fetchSearchData(): Promise<PlayResponse<List<Result>> | PlayError> {
      return Promise.reject({ error: 400 })
    },
    getSearchFilterValueConfigs(filter: RegularFilter): RegularFilterValueConfig[] {
      return filter.config.values.filter((filterValueConfig: RegularFilterValueConfig) =>
        filter.selectedValues.includes(filterValueConfig.id),
      )
    },
    getSearchFilterSelectedValues(filter: RegularFilter): RegularFilterSelectedValues {
      let filterSelectedValues = this.searchFiltersSelectedValues[filter.config.id] || []
      if (!filterSelectedValues.length && !!filter.config.defaultValueId) {
        filterSelectedValues = [filter.config.defaultValueId]
      }
      filterSelectedValues = filterSelectedValues.filter((filterSelectedValue: string) =>
        filter.config.values.some(
          (filterValueConfig: RegularFilterValueConfig) =>
            filterValueConfig.id === filterSelectedValue,
        ),
      )
      if (!filterSelectedValues.length && !!filter.config.values.length) {
        filterSelectedValues = [filter.config.values[0].id]
      }
      return filterSelectedValues
    },
    isRankingElementMatchingWithFilters(element: RankingElement): boolean {
      return Object.entries(element.filters).every(
        ([elementFilterId, elementFilterValues]: [string, string[]]) => {
          if (elementFilterValues.length) {
            const searchFilter: RegularFilter = this.searchFilters.find(
              (filter: RegularFilter) =>
                (filter.config.toolingId ?? filter.config.id) === elementFilterId,
            )
            if (searchFilter) {
              return searchFilter.selectedValues.some((selectedValue: string) => {
                const selectedValueConfig = searchFilter.config.values.find(
                  (valueConfig: RegularFilterValueConfig) => valueConfig.id === selectedValue,
                )
                return elementFilterValues.includes(
                  selectedValueConfig.taggingId || selectedValueConfig.id,
                )
              })
            }
          }
          return true
        },
      )
    },
    isRankingElementMatchingWithDate(element: RankingElement): boolean {
      if (!this.searchDate) {
        return true
      }
      const scheduleToCompare = {
        start: element.schedule.activationDate
          ? new Date(element.schedule.activationDate.getTime())
          : undefined,
        end: element.schedule.deactivationDate
          ? new Date(element.schedule.deactivationDate.getTime())
          : undefined,
      }
      const scheduleToCompareWithoutHours = DateUtils.getPeriodWithoutHours(scheduleToCompare)
      const scheduleToCompareMilliseconds = DomainDateUtils.getPeriodToCompare(
        scheduleToCompareWithoutHours,
      )

      return (
        scheduleToCompareMilliseconds.start <= this.searchDate.getTime() &&
        scheduleToCompareMilliseconds.end >= this.searchDate.getTime()
      )
    },
    isRankingElementMatchingWithPreview(element: RankingElement): boolean {
      return (
        this.isRankingElementMatchingWithFilters(element) &&
        this.isRankingElementMatchingWithDate(element)
      )
    },
    onSearchFilterSelectedValuesChange(
      filter: RegularFilter,
      selectedValues: RegularFilterSelectedValues,
    ): void {
      Vue.set(this.searchFiltersSelectedValues, filter.config.id, selectedValues)
    },
    onPaginationChange(pagination: Pagination): void {
      this.pagination = pagination
      this.loadSearchData({ loadType: SearchDataLoadTypeEnum.RESULTS })
    },
    getFacetActionParamName(facetId: string, action: RankingActionEnum): string {
      const actionParamName = {
        [RankingActionEnum.BOOST]: 'boosts',
        [RankingActionEnum.BURY]: 'buries',
      }[action]
      return `f.${facetId}.${actionParamName}`
    },
    onContextPivotsWatch(): void {
      Object.entries(this.pivots).forEach(([filterId, filter]: [string, RegularFilter]) => {
        if (
          !!this.searchFiltersSelectedValues[filterId] &&
          !filter.selectedValues.some((pivotValue: string) =>
            this.searchFiltersSelectedValues[filterId].includes(pivotValue),
          )
        ) {
          this.searchFiltersSelectedValues[filterId] = []
        }
      })
    },
    async onSearchFiltersWatch(): Promise<void> {
      await this.loadSearchData({ resetPagination: true })
    },
    async onResultsPreviewParamsWatch(): Promise<void> {
      if (!this.loadPreviewDisabled) {
        await this.loadSearchData({ loadType: SearchDataLoadTypeEnum.RESULTS })
      }
    },
    async onFacetsPreviewParamsWatch(): Promise<void> {
      if (!this.loadPreviewDisabled) {
        await this.loadSearchData({ loadType: SearchDataLoadTypeEnum.FACETS })
      }
    },
    onSearchDateChange(date: Date): void {
      this.searchDate = date
    },
  },
  beforeDestroy(): void {
    Object.values(this.watchers).forEach((watcher: () => void) => watcher())
  },
}
